import type { AlignItems, ComponentWidgetConfig, FlexDirection, JustifyContent, WidgetConditionCfg, WidgetType } from 'components/shared/base-product-tile/base-product-tile-types.js'

export type LayoutKind = 'grid' | 'list'

export type MobileConfigMode = 'gridMobile' | 'listMobile'

export type ConfigMode = LayoutKind | MobileConfigMode

export const LAYOUT_KINDS: LayoutKind[] = ['grid', 'list']

export const MOBILE_CONFIG_MODES: MobileConfigMode[] = ['gridMobile', 'listMobile']

export const CONFIG_MODES: ConfigMode[] = [...LAYOUT_KINDS, ...MOBILE_CONFIG_MODES]

export const MODE_LABELS: Record<LayoutKind, string> = {
  grid: 'Grid',
  list: 'List',
}

export const isMobileConfigMode = (mode: ConfigMode): mode is MobileConfigMode => mode === 'gridMobile' || mode === 'listMobile'

export const layoutKindFor = (mode: ConfigMode): LayoutKind => {
  if (mode === 'gridMobile') return 'grid'
  if (mode === 'listMobile') return 'list'
  return mode
}

export const mobileModeFor = (kind: LayoutKind): MobileConfigMode => kind === 'grid' ? 'gridMobile' : 'listMobile'

export interface TileView {
  value: string
  label: string
  mode: LayoutKind
  mobile: boolean
}

export const DEFAULT_TILE_VIEW: TileView = { value: 'grid-desktop', label: 'Grid desktop', mode: 'grid', mobile: false }

export const TILE_VIEWS: TileView[] = [
  DEFAULT_TILE_VIEW,
  { value: 'list-desktop', label: 'List desktop', mode: 'list', mobile: false },
  { value: 'grid-mobile', label: 'Grid mobile', mode: 'grid', mobile: true },
  { value: 'list-mobile', label: 'List mobile', mode: 'list', mobile: true },
]

export const viewTitle = (view: TileView): string => `${view.mobile ? 'Mobile' : 'Desktop'} ${MODE_LABELS[view.mode]}`

export const isPreviewOnlyView = (view: TileView, customized: readonly MobileConfigMode[]): boolean =>
  view.mobile && !customized.includes(mobileModeFor(view.mode))

export const editModeForView = (view: TileView, customized: readonly MobileConfigMode[]): ConfigMode =>
  view.mobile && !isPreviewOnlyView(view, customized) ? mobileModeFor(view.mode) : view.mode

export const writeModeForView = (view: TileView): ConfigMode => view.mobile ? mobileModeFor(view.mode) : view.mode

export interface WidgetCopyOptions {
  skipExistingTypes: boolean
}

// 'image-flow' is 'above'/'below' — a real (always-singular) dynamic zone each, like
// 'product-info'/'product-footer' zones but never splittable into a row/column layout — see
// the note on Section's direction/justifyContent/etc. below.
export type DropAreaType = 'image-overlay' | 'image-flow' | 'product-info' | 'product-footer'

export interface Widget {
  id: string
  type: WidgetType
  section: string
  sequence: number
  position?: 'left' | 'right' | 'top' | 'bottom' | 'center'
  properties: ComponentWidgetConfig
  showWhen?: WidgetConditionCfg[] | undefined
}

export interface Section {
  id: string
  name: string
  children_strategy: 'and' | 'or'
  widgets: Widget[]
  dropAreaType?: DropAreaType
  maxWidgets?: number
  // Only meaningful for a dynamic zone section (dropAreaType 'product-info' | 'product-footer' |
  // 'image-flow') — the zone's own flex container settings, authored via the sidebar zone panel.
  direction?: FlexDirection | undefined
  justifyContent?: JustifyContent | undefined
  alignItems?: AlignItems | undefined
  wrap?: boolean | undefined
  gap?: string | undefined
  // Only meaningful for an image-overlay section (one of the 8 fixed corners) — see
  // ImageOverlayHoverConfig. Hides this zone's widgets until the tile is hovered/focused.
  hideUntilHover?: boolean | undefined
}

// A row is 1 (full width) or 2 (side by side) zone section ids — the editor's own cap, enforced
// by addRow/addColumn, on top of what the underlying DynamicSectionCfg type actually allows.
export type EditorRowLayout = string[]

export interface AreaLayout {
  productInfo: EditorRowLayout[]
  orderActions: EditorRowLayout[]
}

export const MAX_ROWS = 3
export const MAX_COLUMNS_PER_ROW = 2

export const isSectionFull = (section: Section): boolean =>
  !!section.maxWidgets && section.widgets.length >= section.maxWidgets * 3
